<script setup lang="ts">
/**
 * One message.
 *
 * Every affordance below is gated on a capability or on the data itself — never
 * on which surface is rendering. A transport that declares `steps: false` simply
 * has no reasoning UI in the DOM, which is what makes one component serve both
 * an agent run and a plain completion.
 */
import { computed, ref } from 'vue';

import type { ChatCapabilities } from '../types/transport';
import type { ChatMessage, ReasoningStep } from '../types/message';
import ApprovalPrompt from './ApprovalPrompt.vue';
import ContentParts from './ContentParts.vue';
import ThinkingBlock from './ThinkingBlock.vue';
import TokenTicker from './TokenTicker.vue';
import ToolCallCard from './ToolCallCard.vue';

const props = defineProps<{
  message: ChatMessage;
  capabilities: ChatCapabilities;
  busy?: boolean;
  /** Offers a link to this turn's execution trace. Off when the surface has
   *  nowhere to link to — a stateless completion has no task. */
  traceable?: boolean;
  /** Reader preference: keep tool-call cards out of a long transcript. Thinking
   *  is left alone — it explains the answer, where tool calls are plumbing. */
  hideToolCalls?: boolean;
}>();

const emit = defineEmits<{
  approve: [];
  deny: [];
  'submit-result': [output: string, isError: boolean];
  trace: [taskId: string];
}>();

const isUser = computed(() => props.message.role === 'user');
const streaming = computed(() => props.message.status === 'streaming');

const copiedMessage = ref(false);

/** Copies the message's text parts — what the reader sees, not the raw markup. */
async function copyMessage() {
  const text = props.message.content
    .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
    .map((p) => p.text)
    .join('\n\n');
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    copiedMessage.value = true;
    setTimeout(() => {
      copiedMessage.value = false;
    }, 1200);
  } catch {
    /* clipboard unavailable (insecure context / denied) */
  }
}

/**
 * A turn that produced tool calls but no prose is reported as a `thought` whose
 * content is the literal announcement `Calling tool(s): name(args)`. That is
 * plumbing, not reasoning, and the tool card directly below already shows the
 * same call — so it is dropped rather than rendered as the agent's thinking.
 */
const TOOL_ANNOUNCEMENT = /^\s*Calling tool\(s\):/i;

const thoughts = computed(() =>
  (props.message.steps ?? []).filter(
    (s) => s.kind === 'thought' && !TOOL_ANNOUNCEMENT.test(s.content ?? ''),
  ),
);

/** `name({...})` → its parts, for steps that carry the call only as text. */
function parseCallText(text: string): { name?: string; args?: string } {
  const match = /^\s*([A-Za-z_][\w.-]*)\s*\(([\s\S]*)\)\s*$/.exec(text ?? '');
  if (!match) return {};
  const args = match[2].trim();
  return { name: match[1], args: args === 'null' ? '' : args };
}

/**
 * Tool calls paired with their results.
 *
 * `StepUpdate` carries no call id (and often no tool name), so correlating by
 * `toolCallId` alone left every card stuck on "running…" forever — the result
 * could never be matched to its call. Ids are still preferred when present;
 * otherwise calls and results are paired in arrival order, which is the order the
 * tool loop emits them. The name and arguments are recovered from the step text
 * when the typed fields are absent.
 */
const toolCalls = computed(() => {
  const steps = props.message.steps ?? [];
  const calls = steps.filter((s) => s.kind === 'tool_call');
  const results = steps.filter((s) => s.kind === 'tool_result');

  const byId = new Map<string, ReasoningStep>();
  for (const r of results) if (r.toolCallId) byId.set(r.toolCallId, r);

  // First pass: honour real ids. A result may only be claimed once.
  const consumed = new Set<ReasoningStep>();
  const matched = new Map<ReasoningStep, ReasoningStep>();
  for (const call of calls) {
    const hit = call.toolCallId ? byId.get(call.toolCallId) : undefined;
    if (hit && !consumed.has(hit)) {
      matched.set(call, hit);
      consumed.add(hit);
    }
  }

  // Second pass: everything the ids could not correlate, in arrival order.
  // This is the normal path today — when the backend supplies no call_id the
  // mapper synthesises one from the stream sequence, and a call and its result
  // are separate stream entries, so their synthesised ids never agree. Matching
  // on id alone therefore failed for *every* call and left each card running
  // forever.
  const leftovers = results.filter((r) => !consumed.has(r));
  let next = 0;

  return calls.map((call) => {
    const parsed = parseCallText(call.content ?? '');
    const enriched: ReasoningStep = {
      ...call,
      toolName: call.toolName || parsed.name,
      argumentsJson: call.argumentsJson || parsed.args,
    };
    return { call: enriched, result: matched.get(call) ?? leftovers[next++] };
  });
});

const hasContent = computed(() => props.message.content.length > 0);
</script>

<template>
  <div
    data-testid="message-bubble"
    :data-role="message.role"
    :data-status="message.status"
    class="group flex gap-2"
    :class="isUser ? 'justify-end' : 'justify-start'"
  >
    <!-- Assistant avatar only. A second avatar echoing the user back at
         themselves adds a column of noise and no information. -->
    <div
      v-if="!isUser"
      class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-[11px] font-semibold text-white"
      aria-hidden="true"
    >
      AI
    </div>

    <div class="max-w-[85%] space-y-2" :class="isUser ? 'items-end' : 'items-start'">
      <!-- Reasoning precedes the answer: it happened first, and burying the
           answer under it is why this collapses once the turn settles. -->
      <ThinkingBlock
        v-if="capabilities.steps && thoughts.length"
        :steps="thoughts"
        :streaming="streaming"
      />

      <ToolCallCard
        v-for="({ call, result }, i) in capabilities.steps && !hideToolCalls ? toolCalls : []"
        :key="call.id ?? i"
        :call="call"
        :result="result"
      />

      <div
        v-if="hasContent || streaming"
        class="rounded-2xl px-4 py-2"
        :class="
          isUser
            ? 'bg-blue-600 text-white'
            : message.status === 'error'
              ? 'bg-red-50 text-red-900 dark:bg-red-900/20 dark:text-red-200'
              : message.status === 'cancelled'
                ? 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
        "
      >
        <ContentParts :parts="message.content" :markdown="!isUser" />
        <span
          v-if="streaming"
          data-testid="streaming-caret"
          class="animate-pulse"
          aria-hidden="true"
          >▍</span
        >
      </div>

      <!-- Revealed on hover so a settled transcript stays clean. Assistant only:
           the user already has their own text. -->
      <div v-if="!isUser && hasContent && !streaming" class="flex justify-start">
        <button
          type="button"
          data-testid="copy-message"
          class="rounded px-1.5 py-0.5 text-[10px] text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-600 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          @click="copyMessage"
        >
          {{ copiedMessage ? 'Copied' : 'Copy' }}
        </button>
      </div>

      <p
        v-if="message.status === 'error' && message.error"
        data-testid="message-error"
        class="text-xs text-red-600 dark:text-red-400"
      >
        {{ message.error }}
      </p>
      <p
        v-else-if="message.status === 'cancelled'"
        data-testid="message-cancelled"
        class="text-xs text-gray-400"
      >
        Cancelled
      </p>

      <ApprovalPrompt
        v-if="capabilities.approvals && message.approval"
        :approval="message.approval"
        :busy="busy"
        @approve="emit('approve')"
        @deny="emit('deny')"
        @submit-result="(o, e) => emit('submit-result', o, e)"
      />

      <div class="flex items-center gap-2">
        <TokenTicker v-if="message.usage" :usage="message.usage" />
        <button
          v-if="traceable && message.taskId"
          type="button"
          data-testid="trace-link"
          class="text-[10px] text-gray-400 underline hover:text-gray-600 dark:hover:text-gray-300"
          @click="emit('trace', message.taskId)"
        >
          View trace
        </button>
      </div>
    </div>
  </div>
</template>
